import { IconCheckDefault, IconHourglass } from '@iwoca/icons';
import { Pill } from '@iwoca/orion';
import { clsx } from 'clsx';

import { ProfileStage } from './profileStages';
import { displayCurrency } from '../../../../utils/DisplayCurrency';
import { CustomerRow } from '../../CustomersV2.types';

const StageBubble = ({ stage }: { stage: ProfileStage }) => {
  if (stage.status === 'complete') {
    return (
      <div className="flex h-[30px] w-[30px] items-center justify-center rounded-full bg-primary-20 text-structure-100">
        <IconCheckDefault width={24} height={24} aria-hidden />
      </div>
    );
  }

  return (
    <div
      className={clsx(
        'flex h-[30px] w-[30px] items-center justify-center rounded-full text-l font-bold text-structure-100',
        stage.status === 'current'
          ? 'border-[3px] border-secondary-60 bg-primary-20'
          : 'bg-structure-80',
      )}
    >
      {stage.number}
    </div>
  );
};

const StepRow = ({
  label,
  completed,
}: {
  label: string;
  completed: boolean;
}) => (
  <div
    className={clsx(
      'flex min-h-[40px] items-center gap-s text-m',
      completed ? 'font-med text-primary-10' : 'font-reg text-structure-60',
    )}
  >
    {completed ? (
      <IconCheckDefault
        width={24}
        height={24}
        className="shrink-0"
        aria-hidden
      />
    ) : (
      <IconHourglass width={24} height={24} className="shrink-0" aria-hidden />
    )}
    {label}
  </div>
);

const SecurityHeadline = ({ row }: { row: CustomerRow }) => {
  if (row.limit_amount === null) return null;

  return (
    <div className="flex flex-wrap items-baseline gap-x-xs text-m font-bold text-primary-10">
      <span>Pay</span>
      <span className="whitespace-nowrap text-xl text-success-mid">
        {displayCurrency(row.limit_amount)}
      </span>
      {row.limit_duration !== null && (
        <span>
          in{' '}
          {row.limit_duration === 1
            ? '30 days'
            : `${row.limit_duration} monthly instalments`}
        </span>
      )}
    </div>
  );
};

const STAGE_BODY_COPY = {
  business_details:
    'The customer has entered their email address and created an account to checkout. They now need to complete the steps below.',
  security:
    'Just a few final security checks before they are ready to use their limit.',
} as const;

export const StageTimeline = ({
  stages,
  row,
}: {
  stages: ProfileStage[];
  row: CustomerRow;
}) => (
  <div className="grid grid-cols-[30px_1fr] gap-x-s pl-s pt-xl">
    {stages.map((stage, index) => {
      // The pill qualifies the amount in SecurityHeadline; with no amount there
      // is nothing for it to qualify.
      const showLimitPill =
        stage.key === 'security' &&
        stage.status === 'current' &&
        row.limit_amount !== null;

      return (
        <div key={stage.key} className="col-span-2 grid grid-cols-subgrid">
          <div className="flex flex-col items-center">
            <StageBubble stage={stage} />
            {stage.status !== 'upcoming' && (
              <div className="w-[4px] grow bg-primary-20" />
            )}
          </div>
          <div
            className={clsx(
              'flex min-h-[30px] flex-wrap items-center justify-between gap-s text-m',
              stage.status === 'current'
                ? 'font-bold text-primary-10'
                : 'font-med text-structure-40',
            )}
          >
            {stage.label}
            {showLimitPill && (
              <Pill
                variant="london"
                size="small"
                className="h-max w-max shrink-0 whitespace-nowrap text-xs font-med"
              >
                Subject to passing checks
              </Pill>
            )}
          </div>

          {stage.status === 'current' ? (
            <>
              <div className="w-[4px] justify-self-center bg-primary-20" />
              {/* Body copy sits 8px below the headline; the progress count is a
                  further 24px down; the trailing 8px plus each step row's own
                  padding makes the 16px before the first step. */}
              <div className="flex flex-col gap-s pb-s text-m">
                {stage.key === 'security' && <SecurityHeadline row={row} />}
                <div className="font-reg text-primary-10 max-1024:text-s">
                  {STAGE_BODY_COPY[stage.key]}
                </div>
                <div className="mt-xl font-bold text-primary-10">
                  {stage.completedCount}/{stage.requiredCount} steps completed
                </div>
              </div>

              {/* Each step owns its rail segment so the navy run stops halfway
                  through the first incomplete step, as the design specifies. */}
              {stage.steps.map((step, stepIndex) => {
                const firstIncomplete =
                  stage.steps.findIndex((candidate) => !candidate.completed) ===
                  stepIndex;

                return (
                  <div
                    key={step.key}
                    className="col-span-2 grid grid-cols-subgrid"
                  >
                    {firstIncomplete ? (
                      <div className="flex w-[4px] flex-col justify-self-center">
                        <div className="flex-1 bg-primary-20" />
                        <div className="flex-1 bg-structure-80" />
                      </div>
                    ) : (
                      <div
                        className={clsx(
                          'w-[4px] justify-self-center',
                          step.completed ? 'bg-primary-20' : 'bg-structure-80',
                        )}
                      />
                    )}
                    <StepRow label={step.label} completed={step.completed} />
                  </div>
                );
              })}

              {index === stages.length - 1 ? (
                <>
                  <div className="h-2xl w-[4px] justify-self-center bg-structure-80" />
                  <div />
                </>
              ) : (
                <>
                  <div className="h-xl w-[4px] justify-self-center bg-structure-80" />
                  <div className="h-xl" />
                </>
              )}
            </>
          ) : (
            index < stages.length - 1 && (
              <>
                <div
                  className={clsx(
                    'h-xl w-[4px] justify-self-center',
                    stage.status === 'complete'
                      ? 'bg-primary-20'
                      : 'bg-structure-80',
                  )}
                />
                <div className="h-xl" />
              </>
            )
          )}
        </div>
      );
    })}
  </div>
);
